import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  CheckCircle,
  Cancel,
  Visibility,
  Send,
  Replay,
  Edit,
  PictureAsPdf,
  Schedule,
  QrCode2,
  AccessTime,
  CallSplit,
  Forum,
  NotificationsActive,
  HourglassEmpty,
  TaskAlt,
} from "@material-ui/icons";
import { propuestasApi } from "src/services/propuestasApi";
import { formatearFecha } from "src/utils/formatTime";
import { BRAND } from "src/theme/brandTokens";

// El backend entrega metadata como string JSON. Parseo defensivo a objeto o null.
const parseMetadata = (ev) => {
  let md = ev?.metadata;
  if (typeof md === "string") {
    try {
      md = JSON.parse(md);
    } catch {
      md = null;
    }
  }
  return md || null;
};

const CONFIG_EVENTO = {
  envio: { label: "Enviada al cliente", color: "#1565C0", Icon: Send },
  primera_vista: {
    label: "Vista por primera vez",
    color: "#7B1FA2",
    Icon: Visibility,
  },
  vista: { label: "Vista", color: "#9C27B0", Icon: Visibility },
  reenvio: { label: "Reenviada", color: "#1565C0", Icon: Send },
  extension: { label: "Extendida", color: "#F57F17", Icon: AccessTime },
  reactivacion: {
    label: "Reactivada",
    color: BRAND.color.primary,
    Icon: Replay,
  },
  pdf_modificado: { label: "PDF modificado", color: "#455A64", Icon: Edit },
  pdf_firmado: { label: "PDF firmado", color: "#1B5E20", Icon: PictureAsPdf },
  verificacion_qr: { label: "QR verificado", color: "#00695C", Icon: QrCode2 },
  aceptada: {
    label: (ev) =>
      parseMetadata(ev)?.tipo_aceptacion === "INTERNA"
        ? "Aceptada internamente"
        : "Aceptada",
    color: "#1B5E20",
    Icon: CheckCircle,
  },
  rechazada: { label: "Rechazada", color: "#B71C1C", Icon: Cancel },
  parcialmente_aceptada: {
    label: (ev) =>
      parseMetadata(ev)?.tipo_aceptacion === "INTERNA"
        ? "Parcialmente aceptada (interna)"
        : "Parcialmente aceptada",
    color: "#F57F17",
    Icon: CheckCircle,
  },
  clonada: {
    // Label dinámico según metadata.tipo_origen.
    label: (ev) => {
      const md = parseMetadata(ev);
      if (md?.tipo_origen === "DESDE_RECHAZADOS")
        return "Clonada desde rechazados";
      if (md?.tipo_origen === "DUPLICADA") return "Duplicada";
      return "Clonada";
    },
    color: "#00695C",
    Icon: CallSplit,
  },
  // Los cinco tipos siguientes salen del enum de tipo_evento en la base de
  // datos, no de lo que hoy se ve en pantalla: recordatorio y caducada los
  // va a escribir un job del cron que todavía no existe. Si se dejan fuera
  // hasta que ese job arranque, esos eventos caen en la configuración por
  // defecto ("Evento") sin que nadie recuerde por qué.
  renegociacion_abierta: {
    label: "Renegociación abierta",
    color: "#F57F17",
    Icon: Forum,
  },
  renegociacion_ronda_enviada: {
    label: "Nueva ronda enviada",
    color: "#1565C0",
    Icon: Send,
  },
  renegociacion_recordatorio: {
    label: "Recordatorio enviado",
    color: "#F57F17",
    Icon: NotificationsActive,
  },
  renegociacion_caducada: {
    label: "Renegociación caducada",
    color: "#757575",
    Icon: HourglassEmpty,
  },
  cierre_explicito: {
    label: "Cierre explícito",
    color: "#455A64",
    Icon: TaskAlt,
  },
};

const DEFAULT_CONFIG = { label: "Evento", color: "#9E9E9E", Icon: Schedule };

const formatFechaRelativa = (fecha) => {
  if (!fecha) return "—";
  try {
    return formatearFecha(fecha, "DD [de] MMM YYYY, HH:mm");
  } catch {
    return String(fecha);
  }
};

const DotEvento = ({ color, Icon, isLast }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      flexShrink: 0,
      mr: 1.5,
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        bgcolor: color,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 16 }} />
    </Box>
    {!isLast && (
      <Box
        sx={{
          width: 2,
          flex: 1,
          bgcolor: "divider",
          mt: 0.5,
          minHeight: 20,
        }}
      />
    )}
  </Box>
);

DotEvento.propTypes = {
  color: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
  isLast: PropTypes.bool.isRequired,
};

/**
 * Timeline vertical compacto de eventos de una propuesta.
 * Agrupa eventos "vista" consecutivos mostrando el contador (ej. "Vista (3x)").
 * El resto se muestran individuales en orden descendente (más reciente arriba).
 */
const TrackingTimeline = ({ propuestaId }) => {
  const { data, isLoading, error } = propuestasApi.useTrackingPropuestaQuery({
    id: propuestaId,
  });

  const eventosProcesados = useMemo(() => {
    if (!data?.historial) return [];
    // Agrupa vistas consecutivas (respetando el orden del backend desc)
    const resultado = [];
    for (const ev of data.historial) {
      const ultimo = resultado[resultado.length - 1];
      if (ev.tipo_evento === "vista" && ultimo?.tipo_evento === "vista") {
        ultimo.contador = (ultimo.contador ?? 1) + 1;
        // Mantiene la fecha más reciente (que ya está arriba por el orden desc)
        continue;
      }
      resultado.push({ ...ev, contador: 1 });
    }
    return resultado;
  }, [data]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={24} sx={{ color: BRAND.color.primary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" color="error">
        No se pudo cargar el historial.
      </Typography>
    );
  }

  if (eventosProcesados.length === 0) {
    return (
      <Typography variant="caption" color="textSecondary">
        Sin eventos registrados aún.
      </Typography>
    );
  }

  return (
    <Stack spacing={0}>
      {eventosProcesados.map((ev, idx) => {
        const conf = CONFIG_EVENTO[ev.tipo_evento] ?? DEFAULT_CONFIG;
        const isLast = idx === eventosProcesados.length - 1;
        const labelRaw =
          typeof conf.label === "function" ? conf.label(ev) : conf.label;
        const labelConContador =
          ev.contador > 1 ? `${labelRaw} (${ev.contador}x)` : labelRaw;
        const md = parseMetadata(ev);
        const esInterna = md?.tipo_aceptacion === "INTERNA";
        return (
          <Stack
            key={ev.id ?? idx}
            direction="row"
            alignItems="flex-start"
            sx={{ mb: isLast ? 0 : 1.5 }}
          >
            <DotEvento color={conf.color} Icon={conf.Icon} isLast={isLast} />
            <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {labelConContador}
              </Typography>
              <Tooltip title={ev.ip ? `IP: ${ev.ip}` : ""} placement="top">
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block" }}
                >
                  {formatFechaRelativa(ev.fecha_evento)}
                </Typography>
              </Tooltip>
              {esInterna && (
                <Box sx={{ mt: 0.5 }}>
                  {md?.empleado_interno && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block" }}
                    >
                      Registrada por {md.empleado_interno}
                    </Typography>
                  )}
                  {md?.comentarios_aceptacion && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block", fontStyle: "italic" }}
                    >
                      “{md.comentarios_aceptacion}”
                    </Typography>
                  )}
                  {md?.url_archivo_respaldo && (
                    <Link
                      href={md.url_archivo_respaldo}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      sx={{ color: BRAND.color.primary, fontWeight: 600 }}
                    >
                      Ver comprobante
                    </Link>
                  )}
                </Box>
              )}
            </Box>
          </Stack>
        );
      })}
      {data?.diasRestantes != null && data.diasRestantes > 0 && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ mt: 1.5, fontStyle: "italic" }}
        >
          Expira en {data.diasRestantes} día
          {data.diasRestantes === 1 ? "" : "s"}.
        </Typography>
      )}
    </Stack>
  );
};

TrackingTimeline.propTypes = {
  propuestaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default TrackingTimeline;
